// components/dashboard/TrialBanner.js
import React, { useState, useEffect } from 'react';
import { Zap, X, ArrowRight } from 'lucide-react';

export const TrialBanner = ({ api }) => {
  const [trialStatus, setTrialStatus] = useState(null);
  const [isVisible, setIsVisible] = useState(true);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchTrialStatus();
  }, []);

  const fetchTrialStatus = async () => {
    try {
      const status = await api.getTrialStatus();
      setTrialStatus(status);
      
      // Auto-hide banner if user has active subscription
      if (status.hasActiveSubscription) {
        setIsVisible(false);
      }
    } catch (error) {
      console.error('Failed to fetch trial status:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleUpgrade = () => {
    window.location.href = '/pricing';
  };

  const handleDismiss = () => {
    setIsVisible(false);
    // Store dismissal in localStorage with expiry
    const dismissedUntil = Date.now() + (24 * 60 * 60 * 1000); // 24 hours
    localStorage.setItem('trialBannerDismissed', dismissedUntil.toString());
  };

  // Check if banner was previously dismissed
  useEffect(() => {
    const dismissedUntil = localStorage.getItem('trialBannerDismissed');
    if (dismissedUntil && parseInt(dismissedUntil) > Date.now()) {
      setIsVisible(false);
    }
  }, []);

  if (loading || !isVisible || !trialStatus) return null;
  if (trialStatus.hasActiveSubscription) return null;

  const { daysLeft = 7, isExpired = false } = trialStatus;
  
  // Determine urgency level for messaging
  const getTrialMessage = () => {
    if (isExpired) {
      return "Trial Expired - Upgrade to restore full access";
    }
    if (daysLeft <= 1) {
      return "Last day of trial - Don't miss out!";
    }
    if (daysLeft <= 3) {
      return `Only ${daysLeft} days left - Secure your account`;
    }
    return `${daysLeft} days remaining in trial`;
  };

  // Dynamic urgency-based styling
  const isUrgent = daysLeft <= 3 || isExpired;

  return (
    <div className={`${isUrgent ? 'bg-gradient-to-r from-red-500 to-pink-500' : 'bg-gradient-to-r from-indigo-500 to-purple-600'}`}>
      <div className="px-6 py-3">
        <div className="flex items-center justify-between">
          {/* Left side - Icon + Trial info */}
          <div className="flex items-center space-x-3">
            {/* Icon with animated pulse for urgency */}
            <div className="flex-shrink-0">
              <div className={`w-8 h-8 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center ${isUrgent ? 'animate-pulse' : ''}`}>
                <Zap className="w-4 h-4 text-white" />
              </div>
            </div>
            
            {/* Trial text */}
            <div className="flex items-center space-x-2">
              <span className="text-sm font-semibold text-white">
                Free Trial
              </span>
              <span className="text-sm text-white/90">
                • {getTrialMessage()}
              </span>
            </div>
          </div>

          {/* Right side - CTA + Dismiss */}
          <div className="flex items-center space-x-3">
            {/* Upgrade CTA Button */}
            <button
              onClick={handleUpgrade}
              className={`
                group flex items-center space-x-2 px-5 py-2 rounded-md text-sm font-semibold
                transition-all duration-200 transform
                ${isUrgent 
                  ? 'bg-white text-red-600 hover:bg-gray-50 shadow-lg hover:shadow-xl hover:scale-105' 
                  : 'bg-white text-indigo-600 hover:bg-gray-50 shadow-md hover:shadow-lg hover:scale-105'
                }
              `}
            >
              <span>Upgrade Now</span>
              <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-0.5 transition-transform" />
            </button>

            {/* Dismiss button - hide for expired trials */}
            {!isExpired && (
              <button
                onClick={handleDismiss}
                className="p-1 rounded-md text-white/80 hover:text-white hover:bg-white/10 transition-all"
                aria-label="Dismiss banner"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default TrialBanner;